import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { SocialPost, SocialPostTarget } from "@shared/schema";
import { Pencil, Trash2 } from "lucide-react";
import { PostComposerDialog } from "./post-composer-dialog";

interface PostDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  post: SocialPost | null;
}

export function PostDetailDialog({ open, onOpenChange, post }: PostDetailDialogProps) {
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { data: targets } = useQuery<SocialPostTarget[]>({
    queryKey: ["/api/social/posts", post?.id, "targets"],
    enabled: !!post && open,
  });

  const del = useMutation({
    mutationFn: async () => {
      if (!post) return;
      const res = await apiRequest("DELETE", `/api/social/posts/${post.id}`);
      return res.ok ? true : false;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/social/posts"] });
      toast({
        title: "Post moved to trash",
        description: "It will stay recoverable for 7 days before permanent deletion.",
      });
      setConfirmDelete(false);
      onOpenChange(false);
    },
    onError: (err: any) => {
      toast({
        title: "Could not delete",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  if (!post) return null;

  return (
    <>
      <Dialog open={open && !editing} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-2xl" data-testid="dialog-post-detail">
          <DialogHeader>
            <DialogTitle data-testid="text-detail-title">
              {post.campaignTag || "Untitled draft"}
            </DialogTitle>
            <DialogDescription className="flex items-center gap-2">
              <Badge variant="outline" data-testid="badge-detail-status">{post.status}</Badge>
              {post.scheduledAt ? (
                <span className="text-xs">
                  Scheduled for {new Date(post.scheduledAt).toLocaleString()}
                </span>
              ) : (
                <span className="text-xs">Not scheduled</span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Caption</div>
              <div className="whitespace-pre-wrap rounded-md border bg-muted/30 p-3 text-sm" data-testid="text-detail-caption">
                {post.internalNote || <span className="text-muted-foreground italic">No caption yet.</span>}
              </div>
            </div>

            <Separator />

            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                Platforms / accounts
              </div>
              {targets && targets.length > 0 ? (
                <ul className="space-y-1 text-sm">
                  {targets.map((t) => (
                    <li key={t.id} className="flex items-center justify-between border rounded-md p-2" data-testid={`row-target-${t.id}`}>
                      <span className="capitalize">{t.platform}</span>
                      <Badge variant="outline">{t.publishStatus}</Badge>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-sm text-muted-foreground">
                  No platform routing yet. Once you connect accounts, you can choose where this
                  post will publish.
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2 sm:justify-between">
            <Button
              type="button"
              variant="destructive"
              onClick={() => setConfirmDelete(true)}
              data-testid="button-delete-post"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                data-testid="button-close-detail"
              >
                Close
              </Button>
              <Button type="button" onClick={() => setEditing(true)} data-testid="button-edit-post">
                <Pencil className="h-4 w-4 mr-2" />
                Edit
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PostComposerDialog
        open={editing}
        onOpenChange={(o) => {
          setEditing(o);
          if (!o) onOpenChange(false);
        }}
        post={post}
      />

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid="dialog-confirm-delete-post">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this post?</AlertDialogTitle>
            <AlertDialogDescription>
              The post will be moved to trash and recoverable for 7 days before permanent deletion.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => del.mutate()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
